import { useState, useRef } from "react";
import { Controller } from "react-hook-form";
import type { UseFormTrigger } from "react-hook-form";
import {
  Box,
  InputAdornment,
  TextField,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Avatar,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { validatePhoneNumber } from "@/lib/utils";

export interface CountryOption {
  CountryId: number;
  Name: string;
  CountryCode: string;
}

interface PhoneNumberInputProps {
  control: any;
  numberFieldName: string;
  dialCodeFieldName: string;
  label: string;
  required?: boolean;
  maxLength?: number;
  countries: CountryOption[];
  defaultDialCode?: string;
  size?: "small" | "medium";
  variant?: "standard" | "outlined" | "filled";
  endAdornment?: React.ReactNode;
  debounceMs?: number;
  sx?: any;
  requireMobile?: boolean;
  trigger?: UseFormTrigger<any>;
}

const initials = (name: string) => name.slice(0, 2).toUpperCase();

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  control,
  numberFieldName,
  dialCodeFieldName,
  label,
  required,
  maxLength = 15,
  countries,
  defaultDialCode = "+91",
  size = "small",
  variant = "standard",
  endAdornment,
  sx,
  requireMobile = false,
  trigger,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const dialCodeRef = useRef(defaultDialCode);
  const open = Boolean(anchorEl);

  const uniqueByDial = Array.from(
    new Map((countries || []).map((c) => [c.CountryCode, c])).values()
  );

  const filtered = uniqueByDial.filter(
    (c) =>
      c.Name.toLowerCase().includes(search.toLowerCase()) ||
      c.CountryCode.includes(search)
  );

  return (
    <Controller
      name={dialCodeFieldName}
      control={control}
      defaultValue={defaultDialCode}
      render={({ field: dialField }) => {
        if (dialField.value) {
          dialCodeRef.current = dialField.value;
        }

        const currentDialCode = dialField.value || defaultDialCode;

        const selected =
          uniqueByDial.find((c) => c.CountryCode === dialField.value) ||
          uniqueByDial.find((c) => c.CountryCode === defaultDialCode);

        const sortedFiltered = [...filtered].sort((a, b) => {
          if (a.CountryCode === currentDialCode) return -1;
          if (b.CountryCode === currentDialCode) return 1;
          return 0;
        });

        return (
          <Controller
            name={numberFieldName}
            control={control}
            rules={{
              required: required ? `${label} is required` : false,
              validate: (value) => {
                return validatePhoneNumber(
                  value,
                  dialCodeRef.current || dialField.value || defaultDialCode,
                  label,
                  requireMobile
                );
              },
            }}
            render={({ field: numField, fieldState }) => (
              <Box>
                <TextField
                  {...numField}
                  value={numField.value ?? ""}
                  variant={variant}
                  label={label}
                  size={size}
                  fullWidth
                  required={required}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{
                    "& .MuiInputLabel-root": { fontSize: "13px" },
                    "& .MuiInput-root": { fontSize: "14px" },
                    ...sx,
                  }}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    numField.onChange(digits.slice(0, maxLength));
                  }}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "pointer",
                              pr: 1,
                              mr: 0.5,
                              borderRight: "1px solid",
                              borderColor: "divider",
                              userSelect: "none",
                              pb: "2px",
                            }}
                          >
                            <Avatar
                              sx={{ width: 18, height: 18, fontSize: "9px", bgcolor: "primary.main" }}
                            >
                              {selected ? initials(selected.Name) : ""}
                            </Avatar>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                              {selected?.CountryCode || defaultDialCode}
                            </Typography>
                            <KeyboardArrowDownIcon sx={{ color: "text.disabled", fontSize: 15 }} />
                          </Box>
                        </InputAdornment>
                      ),
                      endAdornment,
                    },
                    htmlInput: { maxLength },
                  }}
                />

                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={() => {
                    setAnchorEl(null);
                    setSearch("");
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <Box sx={{ p: 1, width: 260 }}>
                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      placeholder="Search country"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <List sx={{ maxHeight: 240, overflowY: "auto" }}>
                      {sortedFiltered.map((c) => {
                        const isSelected = c.CountryCode === currentDialCode;
                        return (
                          <ListItemButton
                            key={c.CountryId ?? c.CountryCode}
                            selected={isSelected}
                            sx={{
                              borderRadius: 1,
                              my: 0.25,
                              "&.Mui-selected": {
                                backgroundColor: "action.selected",
                                "&:hover": {
                                  backgroundColor: "action.selected",
                                },
                              },
                            }}
                            onClick={() => {
                              dialCodeRef.current = c.CountryCode;
                              dialField.onChange(c.CountryCode);
                              setAnchorEl(null);
                              setSearch("");

                              // Re-validate the number against the newly
                              // selected country immediately.
                              if (numField.value) {
                                if (trigger) {
                                  trigger(numberFieldName);
                                } else {
                                  numField.onChange(numField.value);
                                }
                              }
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 22,
                                height: 22,
                                fontSize: "10px",
                                mr: 1,
                                bgcolor: isSelected ? "primary.main" : "grey.400",
                                color: "#fff",
                              }}
                            >
                              {initials(c.Name)}
                            </Avatar>
                            <ListItemText
                              primary={c.Name}
                              primaryTypographyProps={{
                                fontSize: "13px",
                                fontWeight: isSelected ? 600 : 400,
                                color: isSelected ? "primary.main" : "text.primary",
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "12px",
                                fontWeight: isSelected ? 600 : 400,
                                color: isSelected ? "primary.main" : "text.secondary",
                                mr: isSelected ? 0.5 : 0,
                              }}
                            >
                              {c.CountryCode}
                            </Typography>
                            {isSelected && (
                              <CheckIcon sx={{ fontSize: 16, color: "primary.main", ml: 0.5 }} />
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Box>
                </Popover>
              </Box>
            )}
          />
        );
      }}
    />
  );
};

export default PhoneNumberInput;